import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { useLayoutEffect, useRef, useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { PageTabsList } from "@volca/ui/components/ui/page-tabs-list";
import { Tabs, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { cn } from "@volca/ui/lib/utils";
import { fitTabCount } from "./fit-tabs";
import type { TemplateTab } from "./template-tabs";

const MORE_CLASS =
  "inline-flex h-10 flex-none items-center gap-1 text-sm font-medium whitespace-nowrap";

const TabLabel = ({ tab }: { tab: TemplateTab }) => (
  <>
    {tab.label}
    {tab.count != null && (
      <Badge variant="secondary" size="sm" className="tabular-nums">
        {tab.count.toLocaleString("en-US")}
      </Badge>
    )}
  </>
);

/**
 * Tabs that fit the strip stay in it. The rest open from More, the way a
 * record's tabs collapse once the row runs out of room. A resize observer
 * remeasures when the page narrows or the chat opens beside it.
 */
export const TemplateTabBar = ({
  tabs,
  value,
  onChange,
}: {
  tabs: TemplateTab[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const signature = tabs
    .map((tab) => `${tab.value}:${tab.label}:${tab.count ?? ""}`)
    .join("|");

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const measure = measureRef.current;
    if (!frame || !measure) return;

    const update = () => {
      const widths = [
        ...measure.querySelectorAll<HTMLElement>("[data-tab-measure]"),
      ].map((tab) => tab.offsetWidth);
      const more = measure.querySelector<HTMLElement>("[data-more-measure]");
      const gap = Number.parseFloat(getComputedStyle(measure).columnGap) || 0;
      const next = fitTabCount({
        widths,
        available: frame.clientWidth,
        moreWidth: more?.offsetWidth ?? 0,
        gap,
      });
      setVisibleCount((current) => (current === next ? current : next));
    };

    update();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [signature]);

  const visible = tabs.slice(0, visibleCount);
  const overflow = tabs.slice(visibleCount);
  const overflowTab = overflow.find((tab) => tab.value === value);

  return (
    <Tabs value={value} onValueChange={onChange}>
      <div ref={frameRef} className="relative min-w-0">
        <PageTabsList
          trailing={
            overflow.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger
                  className={cn(
                    MORE_CLASS,
                    "relative cursor-default border-0 bg-transparent p-0 text-subtle outline-none hover:text-ink focus-visible:outline-1 focus-visible:outline-focus",
                    "after:absolute after:inset-x-0 after:bottom-[-5px] after:h-0.5 after:bg-inverse after:opacity-0",
                    overflowTab && "text-ink after:opacity-100",
                  )}
                  aria-label={
                    overflowTab ? `More, ${overflowTab.label}` : "More"
                  }
                >
                  More
                  <CaretDownIcon aria-hidden className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-auto min-w-48">
                  <DropdownMenuRadioGroup
                    value={overflowTab?.value ?? ""}
                    onValueChange={(next) => onChange(next)}
                  >
                    {overflow.map((tab) => (
                      <DropdownMenuRadioItem
                        key={tab.value}
                        value={tab.value}
                        closeOnClick
                      >
                        <TabLabel tab={tab} />
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null
          }
        >
          {visible.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className="gap-1.5"
            >
              <TabLabel tab={item} />
            </TabsTrigger>
          ))}
        </PageTabsList>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-0 overflow-hidden"
        >
          <div ref={measureRef} className="flex w-max gap-4">
            {tabs.map((tab) => (
              <span
                key={tab.value}
                data-tab-measure=""
                className="inline-flex flex-none items-center gap-1.5 text-sm font-medium whitespace-nowrap"
              >
                <TabLabel tab={tab} />
              </span>
            ))}
            <span data-more-measure="" className={MORE_CLASS}>
              More
              <CaretDownIcon aria-hidden className="size-4" />
            </span>
          </div>
        </div>
      </div>
    </Tabs>
  );
};
